import Sider from 'antd/es/layout/Sider';
import { ReactNode, Suspense, lazy, useState } from 'react';
import { SubMenuRoute } from '../../app-bread-crumb.types';
import clsx from 'clsx';
import { sub_sidebar_width } from '@shared/constants';
import { SubmenuOverlay } from '@/container/context/layout/layout-context';
import { RiAddFill, RiChatNewLine, RiSidebarFoldLine } from 'react-icons/ri';
import { AddTeam } from '@modules/teams/components/sidebar-teams/add-team';
import { AddMember } from '@modules/members/components/sidebar-members/add-member';
import SubmenuMembersView from '@modules/members/components/submenu-members/submenu-members-view';
import { Button, Tooltip } from 'antd';
import { Link, useParams } from 'react-router-dom';
import NewConversationModal from '@modules/conversations/new-conversation.modal';
import { useGetCount } from '@shared/hooks/useGetCount';
import { hasRoleAccess } from '@shared/utils/roles/has-role-access.helper';
import { ROLE_MAPS } from '@shared/constants/roles';
import { capitalize } from '@shared/utils/capitalize';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { truncateString } from '@modules/settings/subscription/utils/utils';
const TeamsSubMenu = lazy(() => import('@modules/teams/components/submenu-teams/teams-sub-menu'));
const SettingsMenu = lazy(() => import('./Settings'));
const AiAssistanceMenu = lazy(() => import('./AiAssistance'));
const ChannelsSubMenu = lazy(() => import('@modules/channels/components/submenu-overlay/submenu-channels'));
const AnalyticsMenu = lazy(() => import('./Analytics/Analytics-menu'));
const ConversationsMenu = lazy(() => import('./conversations/conversations-submenu'));
const CalilioHubMenu = lazy(() => import('./CalilioHub'));
const ToolsMenu = lazy(() => import('./Tools'));

type Props = {
  isMobile: boolean;
  openSidebarMobileDrawer: VoidFunction;
  fullWidth: boolean | SubMenuRoute;
  rootPathname: SubMenuRoute | Exclude<SubmenuOverlay, null>;
  channelName: string | undefined;
};

export default function SubMenuSidebar(props: Props) {
  const { isMobile, openSidebarMobileDrawer, fullWidth, rootPathname, channelName } = props;
  const { channelId = '' } = useParams();
  const [openNewConvoModal, setOpenNewConvoModal] = useState(false);
  const getCount = useGetCount();
  const sidebarOpener = (
    <span className={clsx(isMobile ? '' : 'hidden')}>
      <RiSidebarFoldLine onClick={openSidebarMobileDrawer} className="cursor-pointer size-5 text-text-900" />
    </span>
  );
  const { t } = useTranslation();
  const roleAccess = hasRoleAccess([ROLE_MAPS.SETTINGS_MY_NUMBERS_PURCHASE_NUMBER]);
  const renderedConditionalSubMenu = renderConditionalSubMenu(rootPathname, getCount, t, roleAccess);
  return (
    <>
      <Sider
        width={fullWidth ? '100%' : sub_sidebar_width}
        trigger={null}
        theme="light"
        className="border-r border-gray-300 overflow-hidden sticky h-dvh sub-menu"
      >
        <div className="px-4 h-14 border-y border-y-gray-300 justify-between items-center flex">
          <section className="flex gap-2 items-center flex-1">
            {sidebarOpener}
            {rootPathname === 'conversations' ? (
              <div className="flex flex-1 justify-between items-center">
                <p className="truncate max-w-48">{truncateString(channelName, 7)}</p>
                <Tooltip title={capitalize(t('new_message'))} rootClassName="mobile-hide-tooltip">
                  <Button
                    type="text"
                    size="small"
                    className="py-4! rounded-full!"
                    onClick={() => setOpenNewConvoModal(true)}
                  >
                    <RiChatNewLine className="size-5 cursor-pointer text-text-900" />
                  </Button>
                </Tooltip>
              </div>
            ) : (
              <div className="text-text-900 w-full">{renderedConditionalSubMenu.header}</div>
            )}
          </section>
        </div>
        <div className="bg-gray-200 overflow-auto h-[calc(100%-56px)] pb-2">
          <div className="h-full">{renderedConditionalSubMenu.content}</div>
        </div>
      </Sider>
      {openNewConvoModal && (
        <NewConversationModal
          channelId={+channelId}
          open={openNewConvoModal}
          handleClose={() => setOpenNewConvoModal(false)}
        />
      )}
    </>
  );
}
// eslint-disable-next-line react-refresh/only-export-components
export function renderConditionalSubMenu(
  rootPathname: Props['rootPathname'],
  getCount: (name: string) => string | number | undefined,
  t: TFunction,
  roleAccess?: boolean[]
): {
  header: ReactNode;
  content: ReactNode;
} {
  const [canPurchase] = roleAccess ?? [];
  switch (rootPathname) {
    case 'settings':
      return {
        header: capitalize(t('settings')),
        content: (
          <Suspense>
            <SettingsMenu />
          </Suspense>
        ),
      };
    case 'ai-assistance':
      return {
        header: capitalize(t('ai_assistance')),
        content: (
          <Suspense>
            <AiAssistanceMenu />
          </Suspense>
        ),
      };
    case 'analytics':
      return {
        header: capitalize(t('analytics')),
        content: (
          <Suspense>
            <AnalyticsMenu />
          </Suspense>
        ),
      };
    case 'calilio-hub':
      return {
        header: capitalize(t('calilio_hub')),
        content: (
          <Suspense>
            <CalilioHubMenu />
          </Suspense>
        ),
      };
    case 'tools': {
      return {
        header: capitalize(t('tools')),
        content: <Suspense>{<ToolsMenu />}</Suspense>,
      };
    }
    case 'conversations':
      return {
        header: capitalize(t('conversation_other')),
        content: (
          <Suspense>
            <ConversationsMenu />
          </Suspense>
        ),
      };
    case 'channels':
      return {
        header: (
          <div className="grow flex justify-between items-center capitalize!">
            <p>
              {Number(getCount(rootPathname))} {t('number', { count: Number(getCount(rootPathname)) })}
            </p>

            <Tooltip title={capitalize(t('purchase_number'))} rootClassName="mobile-hide-tooltip">
              <Link to="/settings/my-numbers/purchase-number" className="h-9">
                <Button
                  type="text"
                  size="small"
                  disabled={!canPurchase}
                  className="p-0! rounded-full! text-text-600 size-9! hover:bg-gray-300!"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <RiAddFill className="size-5" />
                </Button>
              </Link>
            </Tooltip>
          </div>
        ),
        content: (
          <Suspense>
            <ChannelsSubMenu />
          </Suspense>
        ),
      };
    case 'teams':
      return {
        header: (
          <div className="grow flex justify-between items-center capitalize!">
            <p>
              {Number(getCount(rootPathname))} {t('team', { count: Number(getCount(rootPathname)) })}
            </p>
            <AddTeam isOverlay />
          </div>
        ),
        content: (
          <Suspense>
            <TeamsSubMenu />
          </Suspense>
        ),
      };
    case 'members':
      return {
        header: (
          <div className="grow flex justify-between items-center capitalize!">
            <p>
              {Number(getCount(rootPathname))} {t('member', { count: Number(getCount(rootPathname)) })}
            </p>
            <AddMember isOverlay={true} />
          </div>
        ),
        content: (
          <Suspense>
            <SubmenuMembersView />
          </Suspense>
        ),
      };

    default:
      return {
        header: null,
        content: null,
      };
  }
}
